import { DOCUMENT, inject, Injectable } from '@angular/core';

/**
 * The href of a "Cookie settings" link. It opens a dialog rather than going anywhere, so a click never
 * follows it; behind `<base href="/">` it is also the address a new tab loads: `/#cookie-settings`.
 */
export const COOKIE_SETTINGS_HREF = '#cookie-settings';

/**
 * The part of the cookie consent kit's `window.atConsent` (AT-2746, `src/assets/consent/at-consent.js`)
 * that the app calls. The kit is plain JavaScript loaded by every index shell before GTM, so it carries
 * no types of its own; its full API is in the kit's README.
 */
interface ConsentKit {
  open(): void;
}

type ConsentWindow = Window & {
  atConsent?: ConsentKit;
  /** Set by the index shell's inline snippet, so it is there even when the kit file never arrives. */
  atConsentConfig?: { policyUrl?: string };
};

/**
 * The app's one way into the consent kit (AT-2749).
 *
 * The kit owns the banner, the Cookie settings dialog, the stored choice and Google Consent Mode; the app
 * only has to give people a way back into the dialog, beside the privacy policy and in the account menu.
 * Analytics needs nothing from here: GTM fires its tags from the kit's `at_consent_update` event.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly window = inject(DOCUMENT).defaultView as ConsentWindow | null;

  /**
   * Opens the kit's Cookie settings dialog. When the kit is missing (an ad blocker, a deploy that dropped
   * the asset) nothing optional is running, because the inline default has left it all denied, but the
   * link still has to answer: it goes to the cookie policy, the one page that says what we use. With no
   * inline config either (a unit test), it does nothing.
   */
  openSettings(): void {
    const kit = this.window?.atConsent;
    if (kit) {
      kit.open();
      return;
    }

    const policyUrl = this.window?.atConsentConfig?.policyUrl;
    if (policyUrl) {
      this.window?.location.assign(policyUrl);
    }
  }

  /**
   * Answers a Cookie settings link opened in a new tab. A middle click or "open in new tab" never fires
   * the footer's click handler; the new tab just loads `/#cookie-settings`, so the address is the request.
   *
   * Runs in `provideAppInitializer`, before the router's initial navigation reads the URL, and the
   * fragment comes off first so a reload does not reopen the dialog. The kit's script is synchronous in
   * <head> and the app's bundle is deferred, so the kit is ready by now.
   */
  consumeSettingsLink(): void {
    const view = this.window;
    if (view?.location.hash !== COOKIE_SETTINGS_HREF) {
      return;
    }

    view.history.replaceState(view.history.state, '', view.location.pathname + view.location.search);
    this.openSettings();
  }
}
